import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { LuCheck, LuChevronDown } from "react-icons/lu";

// Dropdown in the Reporting Dashboard filter style: a pill trigger and a
// rounded popover list. Replaces native <select>s, whose OS-drawn menus
// can't be styled.
//
// options: [{ value, label, description?, leading?, disabled? }]
const SelectMenu = ({
  value,
  onChange,
  options,
  placeholder = "Select",
  icon: Icon,
  emptyText = "No options",
  fullWidth = false,
  size = "md", // "md" (h-10) | "sm" (h-8, compact)
  align = "start",
  menuWidth,
  className = "",
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const [pos, setPos] = useState(null);
  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) =>
      ref.current &&
      !ref.current.contains(e.target) &&
      !(menuRef.current && menuRef.current.contains(e.target)) &&
      setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Bring the selected option into view once the menu is positioned
  useEffect(() => {
    if (!open || !pos || !listRef.current) return;
    const el = listRef.current.querySelector('[aria-selected="true"]');
    if (el) listRef.current.scrollTop = el.offsetTop - listRef.current.clientHeight / 2 + el.clientHeight / 2;
  }, [open, pos]);

  // The menu is rendered into <body> (so scrolling containers such as modal
  // bodies can't clip it) and positioned under the trigger.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const btn = ref.current && ref.current.querySelector("button");
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const width = menuWidth || r.width;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < 260 && r.top > spaceBelow;
      setPos({
        left: align === "end" ? Math.max(8, r.right - width) : r.left,
        top: openUp ? undefined : r.bottom + 8,
        bottom: openUp ? window.innerHeight - r.top + 8 : undefined,
        width,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align, menuWidth]);

  const sizeClass =
    size === "sm"
      ? "tw-h-8 tw-rounded-lg tw-border tw-px-2.5 tw-text-xs tw-font-semibold"
      : "tw-h-10 tw-rounded-xl tw-border-2 tw-px-3 tw-text-[13px] tw-font-semibold";

  return (
    <div ref={ref} className={`tw-relative ${fullWidth ? "tw-block" : "tw-inline-block"} ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={`tw-inline-flex tw-items-center tw-justify-between tw-gap-2 tw-border-solid tw-border-slate-100 tw-bg-white tw-text-slate-700 tw-shadow-soft tw-transition-all hover:tw-bg-slate-50 focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-primary ${sizeClass} ${
          fullWidth ? "tw-w-full" : ""
        }`}
      >
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          {Icon && <Icon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-primary" aria-hidden="true" />}
          <span className={`tw-truncate ${selected ? "tw-text-foreground" : "tw-text-slate-400"}`}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <LuChevronDown
          className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400 tw-transition-transform ${open ? "tw-rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="tw-fixed tw-z-[2000] tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-font-sans tw-shadow-2xl"
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}
        >
          {options.length === 0 ? (
            <div className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">{emptyText}</div>
          ) : (
            <ul ref={listRef} role="listbox" className="tw-m-0 tw-max-h-72 tw-list-none tw-overflow-y-auto tw-p-1.5">
              {options.map((o) => {
                const isSelected = String(o.value) === String(value);
                return (
                  <li key={o.value} role="option" aria-selected={isSelected} aria-disabled={o.disabled || undefined}>
                    <button
                      type="button"
                      disabled={o.disabled}
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                      className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border-0 tw-px-2.5 tw-py-2 tw-text-left tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${
                        isSelected ? "tw-bg-primary/10" : "tw-bg-transparent hover:tw-bg-slate-50"
                      }`}
                    >
                      {o.leading}
                      <span className="tw-min-w-0 tw-flex-1">
                        <span
                          className={`tw-block tw-truncate tw-text-sm ${
                            isSelected ? "tw-font-semibold tw-text-primary" : "tw-font-medium tw-text-foreground"
                          }`}
                        >
                          {o.label}
                        </span>
                        {o.description && (
                          <span className="tw-block tw-truncate tw-text-xs tw-text-muted-foreground">{o.description}</span>
                        )}
                      </span>
                      {isSelected && <LuCheck className="tw-shrink-0 tw-text-primary" aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SelectMenu;
